import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetDashboardPaymentEPSListParams } from '@nxsys/store/slices/dashboard/types';
import { ICompany } from '@nxsys/types/models/company';
import { IPresubmitEPS } from '@nxsys/types/models/presubmitEPS';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney, formatMoneyExport } from '@nxsys/utils/number-helper';
import { Dropdown, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SubmitEPSModal, { SubmitEPSValues } from './SubmitEPSModal';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';
import { showMessage } from '@nxsys/utils/message';
import {
  useSubmitCISDeductionsSuffered,
  useSubmitEPS,
} from '@nxsys/hooks/api/dashboard';
import { SubmitEPSParams } from '@nxsys/types/api/dashboard';
import { ExportToCsv } from 'export-to-csv';

type Props = {
  isOpen: boolean;
  toggle: () => void;
};

const EPSModal: React.FC<Props> = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const currentTaxYear = getTaxWeekTaxYear().taxYear;
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const { epsList } = useSelector((state: AppState) => state.dashboardStore);
  const { list, pagination, loading, queryParams } = epsList;

  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
  const [selectedTaxYear, setSelectedTaxYear] = useState<number | null>(currentTaxYear);
  const [selectedTaxMonth, setSelectedTaxMonth] = useState<any>(null);

  const [isSubmitEPSModalOpen, setIsSubmitEPSModalOpen] = useState(false);
  const [isTestSubmitEPSModalOpen, setIsTestSubmitEPSModalOpen] =
    useState(false);
  const [
    isCISDeductionsSufferedModalOpen,
    setIsCISDeductionsSufferedModalOpen,
  ] = useState(false);

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
        companyId: selectedCompany?.id,
        taxYear: selectedTaxYear || undefined,
        taxMonth: selectedTaxMonth?.value || undefined,
        taxMonthStartAt: selectedTaxMonth?.startTaxMonth || undefined,
        taxMonthEndAt: selectedTaxMonth?.endTaxMonth || undefined,
      });
  }, [isOpen, selectedCompany, selectedTaxYear, selectedTaxMonth]);

  const handleFetchData = (params: GetDashboardPaymentEPSListParams) => {
    dispatch(dashboardActions.getDashboardPaymentEPSList(params));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<IPresubmitEPS> = [
    {
      title: 'Amount Due',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.amountDue || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'EPS Due Date',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        props?.epsDueDate
          ? moment(props.epsDueDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
      width: 200,
    },
    {
      title: 'Pay Bill (Gross for NI)',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(props?.payBill || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      title: 'Tax',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(props?.tax || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      title: 'Student Loan',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.studentLoan || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'PG Student Loan',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(props?.pgLoan || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      title: 'SMP Recoveries',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.smpRecovery || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'SPP & SPPA Recoveries',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.sppAndSppaRecoveries || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'SAP Recoveries',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.sapRecovery || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'SNCP Recoveries',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.sncpRecovery || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'SPBP Recoveries',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.spbpRecovery || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'Emp. Allowance Claim',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.empAllowanceClaimed || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'CIS Deduction Suffered',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.cisDeductionSuffered || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'CIS Deduction Suffered YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.cisDeductionSufferedTD || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 250,
    },
    {
      title: 'App Levy Payable',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.appLevyPayable || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'Gross NI',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(props?.grossNI || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      title: 'Recoveries',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.recoveries || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'Net NI',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(props?.netNI || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      title: 'Recoverable Amounts YTD?',
      dataIndex: 'epsColumns',
      align: 'center',
      width: 250,
      render: (props) => (
        <Tag>{props?.recoverableAmountsYTD ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'SMP Recovered YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.SMPRecovered ?? props?.smpRecovery ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'SPP Recovered YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.SPPRecovered ??
            props?.sppAndSppaRecoveries ??
            0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'SAP Recovered YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.SAPRecovered ?? props?.sapRecovery ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'ShPP Recovered YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (_props, record) =>
        formatMoney(
          record?.recoverableAmounts?.ShPPRecovered ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'SPBP Recovered YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.SPBPRecovered ?? props?.spbpRecovery ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'SNCP Recovered YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.SNCPRecovered ?? props?.sncpRecovery ?? 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 200,
    },
    {
      title: 'NIC Compensation On SMP YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.NICCompensationOnSMP ??
            props?.NICCompensationOnSMP ??
            0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 300,
    },
    {
      title: 'NIC Compensation On SPP YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.NICCompensationOnSPP ??
            props?.NICCompensationOnSPP ??
            0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 300,
    },
    {
      title: 'NIC Compensation On SAP YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.NICCompensationOnSAP ??
            props?.NICCompensationOnSAP ??
            0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 300,
    },
    {
      title: 'NIC Compensation On ShPP YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.NICCompensationOnShPP ??
            props?.NICCompensationOnShPP ??
            0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 300,
    },
    {
      title: 'NIC Compensation On SPBP YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.NICCompensationOnSPBP ??
            props?.NICCompensationOnSPBP ??
            0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 300,
    },
    {
      title: 'NIC Compensation On SNCP YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props, record) =>
        formatMoney(
          record?.recoverableAmounts?.NICCompensationOnSNCP ??
            props?.NICCompensationOnSNCP ??
            0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 300,
    },
    {
      title: 'CIS Deductions Suffered YTD',
      dataIndex: 'epsColumns',
      align: 'right',
      render: (props) =>
        formatMoney(
          props?.cisDeductionsSufferedYTD || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      width: 300,
    },
  ];

  const taxMonthOptions = [
    { label: 'Tax Month 1', value: 1, startTaxMonth: `06/04/${currentTaxYear || ''}`, endTaxMonth: `05/05/${currentTaxYear || ''}` },
    { label: 'Tax Month 2', value: 2, startTaxMonth: `06/05/${currentTaxYear || ''}`, endTaxMonth: `05/06/${currentTaxYear || ''}` },
    { label: 'Tax Month 3', value: 3, startTaxMonth: `06/06/${currentTaxYear || ''}`, endTaxMonth: `05/07/${currentTaxYear || ''}` },
    { label: 'Tax Month 4', value: 4, startTaxMonth: `06/07/${currentTaxYear || ''}`, endTaxMonth: `05/08/${currentTaxYear || ''}` },
    { label: 'Tax Month 5', value: 5, startTaxMonth: `06/08/${currentTaxYear || ''}`, endTaxMonth: `05/09/${currentTaxYear || ''}` },
    { label: 'Tax Month 6', value: 6, startTaxMonth: `06/09/${currentTaxYear || ''}`, endTaxMonth: `05/10/${currentTaxYear || ''}` },
    { label: 'Tax Month 7', value: 7, startTaxMonth: `06/10/${currentTaxYear || ''}`, endTaxMonth: `05/11/${currentTaxYear || ''}` },
    { label: 'Tax Month 8', value: 8, startTaxMonth: `06/11/${currentTaxYear || ''}`, endTaxMonth: `05/12/${currentTaxYear || ''}` },
    { label: 'Tax Month 9', value: 9, startTaxMonth: `06/12/${currentTaxYear || ''}`, endTaxMonth: `05/01/${currentTaxYear + 1 || ''}` },
    { label: 'Tax Month 10', value: 10, startTaxMonth: `06/01/${currentTaxYear + 1 || ''}`, endTaxMonth: `05/02/${currentTaxYear + 1 || ''}` },
    { label: 'Tax Month 11', value: 11, startTaxMonth: `06/02/${currentTaxYear + 1 || ''}`, endTaxMonth: `05/03/${currentTaxYear + 1 || ''}` },
    { label: 'Tax Month 12', value: 12, startTaxMonth: `06/03/${currentTaxYear + 1 || ''}`, endTaxMonth: `05/04/${currentTaxYear + 1 || ''}` },
  ];

  const validateBeforeAction = () => {
    if (!selectedCompany || !selectedTaxYear || !selectedTaxMonth) {
      showMessage({
        type: 'warning',
        content: 'Please select company, tax year and tax month',
      });
      return;
    }
    if (!list?.length) {
      showMessage({
        type: 'warning',
        content: 'There is no data to export',
      });
      return;
    }
  };

  const handleOpenSubmitEPSModal = () => {
    validateBeforeAction();
    setIsSubmitEPSModalOpen(true);
  };

  const handleOpenTestSubmitEPSModal = () => {
    validateBeforeAction();
    setIsTestSubmitEPSModalOpen(true);
  };

  const { run: submitCISDeductionsSuffered } = useSubmitCISDeductionsSuffered();
  const { run: submitEPS } = useSubmitEPS();

  const handleSubmitEPS = async (
    values: SubmitEPSValues,
    isTesting = false,
  ) => {
    try {
      await submitCISDeductionsSuffered(selectedCompany!.id, {
        taxYear: selectedTaxYear!,
        taxMonth: selectedTaxMonth!.value,
        cisDeductionsSuffered: values.cisDeductionsSuffered,
      });

      const params: SubmitEPSParams = {
        companyId: selectedCompany!.id,
        taxYear: selectedTaxYear!,
        taxMonth: selectedTaxMonth!.value,
        startTaxMonth: selectedTaxMonth!.startTaxMonth,
        endTaxMonth: selectedTaxMonth!.endTaxMonth,
        finalSubmitType: null,
        dateSchemeCeased: null,
        ignoreValidationDate: isTesting,
      };

      const res = await submitEPS(params);
      if (res?.success) {
        showMessage({
          type: 'success',
          content: 'Submit EPS successfully',
        });
        refetchData();
        window.dispatchEvent(new CustomEvent('dashboard:reload-rti'));
      } else {
        let errorMsg = '';
        try {
          errorMsg =
            typeof res?.data === 'string'
              ? JSON.parse(res?.data)?.message
              : res?.data?.message;
        } catch {
          errorMsg =
            typeof res?.data === 'string'
              ? res.data
              : 'Submit EPS failed';
        }
        showMessage({
          type: 'error',
          content: errorMsg || 'Submit EPS failed',
        });
      }
    } catch (err) {
      showMessage({
        type: 'error',
        content: getErrorMessage(err),
      });
    }
  };

  const handleTestSubmitEPS = async (values: SubmitEPSValues) => {
    await handleSubmitEPS(values, true);
  };

  const handleOpenCISDeductionsSufferedModal = () => {
    validateBeforeAction();
    setIsCISDeductionsSufferedModalOpen(true);
  };

  const handleSubmitCISDeductionsSuffered = async (values: SubmitEPSValues) => {
    await submitCISDeductionsSuffered(selectedCompany!.id, {
      taxYear: selectedTaxYear!,
      taxMonth: selectedTaxMonth!.value,
      cisDeductionsSuffered: values.cisDeductionsSuffered,
    });
    showMessage({
      type: 'success',
      content: 'CIS Deductions Suffered was updated successfully',
    });
    refetchData();
  };

  const handleExport = () => {
    if (!list?.length) {
      showMessage({
        type: 'warning',
        content: 'There is no data to export',
      });
      return;
    }
    const dataExport = list.map((item) => {
      const {
        epsColumns: {
          amountDue,
          appLevyPayable,
          cisDeductionSuffered,
          empAllowanceClaimed,
          epsDueDate,
          grossNI,
          netNI,
          payBill,
          pgLoan,
          recoveries,
          sapRecovery,
          smpRecovery,
          sppAndSppaRecoveries,
          spbpRecovery,
          studentLoan,
          tax,
        },
      } = item;
      return {
        amountDue: formatMoneyExport(
          amountDue,
          showCurrencyByCountry(payrollCountry),
        ),
        epsDueDate: epsDueDate
          ? moment(epsDueDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
        payBill: formatMoneyExport(
          payBill,
          showCurrencyByCountry(payrollCountry),
        ),
        tax: formatMoneyExport(tax, showCurrencyByCountry(payrollCountry)),
        studentLoan: formatMoneyExport(
          studentLoan,
          showCurrencyByCountry(payrollCountry),
        ),
        pgLoan: formatMoneyExport(
          pgLoan,
          showCurrencyByCountry(payrollCountry),
        ),
        smpRecoveries: formatMoneyExport(smpRecovery),
        sppAndSppaRecoveries: formatMoneyExport(
          sppAndSppaRecoveries,
          showCurrencyByCountry(payrollCountry),
        ),
        sapRecoveries: formatMoneyExport(
          sapRecovery,
          showCurrencyByCountry(payrollCountry),
        ),
        spbpRecoveries: formatMoneyExport(
          spbpRecovery,
          showCurrencyByCountry(payrollCountry),
        ),
        empAllowanceClaims: formatMoneyExport(
          empAllowanceClaimed,
          showCurrencyByCountry(payrollCountry),
        ),
        cisDeductionSuffered: formatMoneyExport(
          cisDeductionSuffered,
          showCurrencyByCountry(payrollCountry),
        ),
        appLevy: formatMoneyExport(
          appLevyPayable,
          showCurrencyByCountry(payrollCountry),
        ),
        grossNI: formatMoneyExport(
          grossNI,
          showCurrencyByCountry(payrollCountry),
        ),
        recoveries: formatMoneyExport(
          recoveries,
          showCurrencyByCountry(payrollCountry),
        ),
        netNI: formatMoneyExport(netNI, showCurrencyByCountry(payrollCountry)),
      };
    });
    const headers = [
      'Amount Due',
      'Due Date',
      'Pay Bill (Gross for NI)',
      'Tax',
      'Student Loan',
      'PG Student Loan',
      'SMP Recoveries',
      'SPP & SPP Recoveries',
      'SAP Recoveries',
      'SNCP Recoveries',
      'SPBP Recoveries',
      'Emp. Allowance Claim',
      'CIS Deduction Suffered',
      'App Levy Payable',
      'Gross NI',
      'Recoveries',
      'Net NI',
    ];
    const csvExporter = new ExportToCsv({
      filename:
        `EPS-${moment(new Date()).tz('Europe/London').format('DD/MM/YYYY')}` ||
        'Download.csv',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    csvExporter.generateCsv(dataExport);
  };

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className='whitespace-nowrap'>EPS</div>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'cisDeductionsSuffered',
                  label: 'CIS Deductions Suffered',
                  onClick: handleOpenCISDeductionsSufferedModal,
                },
                {
                  key: 'testSubmit',
                  label: 'Test Submit',
                  onClick: handleOpenTestSubmitEPSModal,
                  // NX-6214: Test Submit sends ignoreValidationDate, which skips the
                  // 6th-19th EPS window check while still filing live with HMRC.
                  // Keep it off outside test environments, as the legacy UI did.
                  disabled: import.meta.env.VITE_IS_TESTING_API !== 'true',
                },
                {
                  key: 'submit',
                  label: 'Submit',
                  onClick: handleOpenSubmitEPSModal,
                },
                {
                  key: 'export',
                  label: 'Export',
                  onClick: handleExport,
                },
              ],
            }}
          >
            <AppButton name="Actions" rightIcon={<DownOutlined />} />
          </Dropdown>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <AppsContent>
        <div className="flex items-center justify-between gap-2 w-full my-[20px] px-[10px] flex-wrap">
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            <AppSelectSync
              type="company"
              apiType="searchCompany"
              placeholder="Select Payment Company"
              onChangeValue={(value: any) => {
                setSelectedCompany(value?.fullData);
              }}
              paramFilter={{ enableRTI: true }}
            />
            <TaxYearSelect
              placeholder="Select Tax Year"
              onFilter={(e) => setSelectedTaxYear(e?.taxYear || null)}
              allowClear
            />
            <AppSelect
              options={taxMonthOptions}
              placeholder="Select Tax Month"
              className="!min-w-0 !w-fit"
              onChange={(value) => setSelectedTaxMonth(value)}
            />
          </div>
        </div>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 4000 }}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
      <SubmitEPSModal
        isOpen={isSubmitEPSModalOpen}
        toggle={() => setIsSubmitEPSModalOpen(false)}
        onSubmit={handleSubmitEPS}
        defaultValues={{
          cisDeductionsSuffered:
            list && list[0] && list[0].epsColumns?.cisDeductionSuffered
              ? list[0].epsColumns?.cisDeductionSuffered
              : 0,
        }}
      />
      <SubmitEPSModal
        isOpen={isTestSubmitEPSModalOpen}
        toggle={() => setIsTestSubmitEPSModalOpen(false)}
        onSubmit={handleTestSubmitEPS}
        defaultValues={{
          cisDeductionsSuffered:
            list && list[0] && list[0].epsColumns?.cisDeductionSuffered
              ? list[0].epsColumns?.cisDeductionSuffered
              : 0,
        }}
      />
      <SubmitEPSModal
        title="CIS Deductions Suffered"
        isOpen={isCISDeductionsSufferedModalOpen}
        toggle={() => setIsCISDeductionsSufferedModalOpen(false)}
        onSubmit={handleSubmitCISDeductionsSuffered}
        defaultValues={{
          cisDeductionsSuffered:
            list && list[0] && list[0].epsColumns?.cisDeductionSuffered
              ? list[0].epsColumns?.cisDeductionSuffered
              : 0,
        }}
      />
    </AppModalContainer>
  );
};

export default EPSModal;
